import React, { useEffect, useState } from 'react';
import {
  Card,
  Form,
  Select,
  DatePicker,
  Typography,
  Divider,
  Spin,
  Checkbox,
  Radio,
  Space,
} from 'antd';
import {
  DownloadOutlined,
  UnorderedListOutlined,
  FileOutlined,
} from '@ant-design/icons';
import {
  DownloadMode,
  DownloadDirectParams,
  DownloadConfigParams,
  DownloadFileListParams,
  DownloadFilePartParams,
  ConfigListItem,
} from '@core/@models/ApiManualModel';
import { ModeToggle } from '../shared/ModeToggle';
import { ConfigSelector } from '../shared/ConfigSelector';
import { CodeSnippetDisplay } from '../shared/CodeSnippetDisplay';
import {
  useDownloadServiceData,
  normalizeDateStr,
} from '../shared/useDownloadServiceData';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);
const { Text } = Typography;

// Download action types
type DownloadAction = 'download-all' | 'list-files' | 'download-specific';

export const DownloadServiceTab: React.FC = () => {
  const {
    data,
    loading,
    selectedGroup,
    setSelectedGroup,
    selectedFileList,
    setSelectedFileList,
    fileLists,
    availableDates,
    beginDate,
    endDate,
    handleDateRangeChange,
    handleCalendarChange,
    isDateInRange,
    createDateRender,
    resetDates,
  } = useDownloadServiceData();

  const [mode, setMode] = useState<DownloadMode>('direct');
  const [action, setAction] = useState<DownloadAction>('download-all');
  const [noDirectory, setNoDirectory] = useState<boolean>(false);
  const [fileName, setFileName] = useState<string>('');
  const [specificDate, setSpecificDate] = useState<string>('');
  const [configName, setConfigName] = useState<string>('');
  const [configGroups, setConfigGroups] = useState<string[]>([]);
  const [datePickerKey, setDatePickerKey] = useState(0);

  // When a config is selected, extract its groups and auto-select if only one
  const handleConfigSelect = (config: ConfigListItem | null) => {
    if (config?.fileGroups && config.fileGroups.length > 0) {
      const groups = config.fileGroups.map((fg) => fg.name);
      setConfigGroups(groups);
      if (groups.length === 1) {
        setSelectedGroup(groups[0]);
      } else {
        setSelectedGroup('');
      }
    } else {
      setConfigGroups([]);
      setSelectedGroup('');
    }
  };

  // Reset all state when mode changes
  useEffect(() => {
    setSelectedGroup('');
    setSelectedFileList('');
    setConfigName('');
    setConfigGroups([]);
    setAction('download-all');
    setNoDirectory(false);
    setSpecificDate('');
    setFileName('');
    resetDates();
    setDatePickerKey((k) => k + 1);
  }, [mode]);

  // Reset dates and specific file selection when action changes
  useEffect(() => {
    setSpecificDate('');
    setFileName('');
    resetDates();
    setDatePickerKey((k) => k + 1);
  }, [action]);

  // Reset specific file selection when group or file category changes
  useEffect(() => {
    setSpecificDate('');
    setFileName('');
    resetDates();
    setDatePickerKey((k) => k + 1);
  }, [selectedGroup, selectedFileList]);

  // Extended date selection check: also considers specific date mode
  const isDateSelected = (dateStr: string): boolean => {
    if (action === 'download-specific') return specificDate === dateStr;
    return isDateInRange(dateStr);
  };

  const dateRender = createDateRender(isDateSelected);

  // --- Computed: available files for selected group + file category ---
  // LIST_TYPE: any date has >1 file → use file.display (actual filenames)
  // FILE_TYPE: all dates have 0-1 files → use slot.name (category identifier)
  const availableFiles: { name: string; date: string }[] = [];
  if (selectedGroup && selectedFileList) {
    const group = data.find((d) => d.name === selectedGroup);
    if (group) {
      const isListType = group.rows.some((row) =>
        row.some(
          (s) => s != null && s.name === selectedFileList && s.files.length > 1
        )
      );

      group.rows.forEach((row) => {
        row.forEach((slot) => {
          if (slot && slot.name === selectedFileList && slot.slotDate) {
            const nd = normalizeDateStr(slot.slotDate);
            if (slot.files.length === 0) return;
            if (isListType) {
              slot.files.forEach((file) => {
                if (file.display) {
                  availableFiles.push({ name: file.display, date: nd });
                }
              });
            } else {
              availableFiles.push({ name: slot.name, date: nd });
            }
          }
        });
      });
    }
  }

  // For list-files: files filtered by selected date range
  const filteredFiles = availableFiles.filter((f) => {
    if (!beginDate || !endDate) return false;
    const d = dayjs(f.date, 'DD/MM/YYYY');
    const start = dayjs(beginDate, 'DD/MM/YYYY');
    const end = dayjs(endDate, 'DD/MM/YYYY');
    return (
      (d.isSame(start) || d.isAfter(start)) &&
      (d.isSame(end) || d.isBefore(end))
    );
  });

  // For download-specific: available dates (newest first)
  const categoryDates = Array.from(
    new Set(availableFiles.map((f) => f.date))
  ).sort(
    (a, b) =>
      dayjs(b, 'DD/MM/YYYY').valueOf() - dayjs(a, 'DD/MM/YYYY').valueOf()
  );

  // For download-specific: files available on the selected date
  const filesForDate = Array.from(
    new Set(
      availableFiles.filter((f) => f.date === specificDate).map((f) => f.name)
    )
  );

  const isFormComplete = () => {
    const commonComplete = selectedGroup && selectedFileList;

    if (mode === 'config') {
      return configName && selectedGroup && beginDate && endDate;
    }

    switch (action) {
      case 'download-all':
      case 'list-files':
        return commonComplete && beginDate && endDate;
      case 'download-specific':
        return commonComplete && fileName && specificDate;
      default:
        return false;
    }
  };

  const getApiType = (): string => {
    if (mode === 'config') {
      return 'download-config';
    }
    switch (action) {
      case 'download-all':
        return 'download-direct';
      case 'list-files':
        return 'download-file-list';
      case 'download-specific':
        return 'download-file-part';
      default:
        return 'download-direct';
    }
  };

  const getParams = ():
    | DownloadDirectParams
    | DownloadConfigParams
    | DownloadFileListParams
    | DownloadFilePartParams => {
    if (mode === 'config') {
      return {
        mode: 'config',
        configName,
        group: selectedGroup,
        beginDate,
        endDate,
        noDirectory,
      };
    }

    switch (action) {
      case 'download-all':
        return {
          mode: 'direct',
          group: selectedGroup,
          fileList: selectedFileList,
          beginDate,
          endDate,
          noDirectory,
        };
      case 'list-files':
        return {
          group: selectedGroup,
          fileList: selectedFileList,
          beginDate,
          endDate,
        };
      case 'download-specific':
        return {
          group: selectedGroup,
          fileList: selectedFileList,
          fileName,
          date: specificDate,
        };
      default:
        return {
          mode: 'direct',
          group: selectedGroup,
          fileList: selectedFileList,
          beginDate,
          endDate,
          noDirectory,
        };
    }
  };

  return (
    <div className="download-service-tab">
      <Card size="small" className="info-card">
        <Text type="secondary">
          Download files from the Download Service. Choose between downloading
          all files, listing available files, or downloading a specific file.
        </Text>
      </Card>

      <Divider />

      <ModeToggle mode={mode} onChange={setMode} />

      <Divider />

      <Spin spinning={loading}>
        <Form layout="vertical">
          {/* Config Mode - Config Selection */}
          {mode === 'config' && (
            <Form.Item label="Saved Config" required>
              <ConfigSelector
                type="download"
                value={configName}
                onChange={setConfigName}
                onConfigSelect={handleConfigSelect}
                placeholder="Select a saved download config"
              />
            </Form.Item>
          )}

          {/* Select Group */}
          <Form.Item label="Group" required>
            <Select
              value={selectedGroup || undefined}
              onChange={setSelectedGroup}
              placeholder={
                mode === 'config' && configGroups.length === 0 && configName
                  ? 'No groups in this config'
                  : 'Select a group'
              }
              showSearch
              optionFilterProp="label"
            >
              {data
                .filter(
                  (group) =>
                    mode !== 'config' ||
                    configGroups.length === 0 ||
                    configGroups.includes(group.name)
                )
                .map((group) => (
                  <Select.Option
                    key={group.name}
                    value={group.name}
                    label={`${group.aliasName || group.name} ${
                      group.description || ''
                    }`}
                  >
                    {group.aliasName || group.name}
                    {group.description && (
                      <span style={{ color: '#888', marginLeft: 8 }}>
                        - {group.description}
                      </span>
                    )}
                  </Select.Option>
                ))}
            </Select>
          </Form.Item>

          {/* Select File Category (Direct Mode only) */}
          {mode === 'direct' && (
            <Form.Item label="File Category" required>
              <Select
                value={selectedFileList}
                onChange={setSelectedFileList}
                placeholder={
                  selectedGroup
                    ? 'Select a file category'
                    : 'Select a group first'
                }
                disabled={!selectedGroup}
                showSearch
                optionFilterProp="label"
              >
                {fileLists.map((fl) => (
                  <Select.Option
                    key={fl.name}
                    value={fl.name}
                    label={`${fl.display} ${fl.name}`}
                  >
                    {fl.display}
                    <span
                      style={{
                        color: '#888',
                        marginLeft: 8,
                        fontSize: '12px',
                      }}
                    >
                      ({fl.name})
                    </span>
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          )}

          {/* Action (Direct Mode only) */}
          {mode === 'direct' && (
            <Form.Item label="Action" required>
              <Radio.Group
                value={action}
                onChange={(e) => setAction(e.target.value)}
                className="action-selection"
              >
                <Space direction="vertical" style={{ width: '100%' }}>
                  {/* Option 1: Download All */}
                  <Card
                    size="small"
                    className={`action-card ${
                      action === 'download-all' ? 'selected' : ''
                    }`}
                    style={{
                      borderColor:
                        action === 'download-all' ? '#FCB034' : '#d9d9d9',
                      backgroundColor:
                        action === 'download-all' ? '#FEF4E9' : '#fff',
                    }}
                  >
                    <Radio value="download-all">
                      <Space>
                        <DownloadOutlined
                          style={{ fontSize: 18, color: '#FCB034' }}
                        />
                        <div>
                          <Text strong>Download all files (ZIP)</Text>
                          <br />
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            GET /external/get/file
                          </Text>
                        </div>
                      </Space>
                    </Radio>

                    {action === 'download-all' && (
                      <div style={{ marginTop: 12, marginLeft: 24 }}>
                        <Form.Item
                          label="Date Range"
                          required
                          style={{ marginBottom: 0 }}
                        >
                          <DatePicker.RangePicker
                            key={`all-${datePickerKey}`}
                            format="DD/MM/YYYY"
                            onChange={handleDateRangeChange}
                            onCalendarChange={handleCalendarChange}
                            style={{ width: '100%' }}
                            dateRender={dateRender}
                          />
                          {availableDates.size > 0 && (
                            <div style={{ marginTop: 4 }}>
                              <Text type="secondary" style={{ fontSize: 11 }}>
                                Dates with green background have available files
                              </Text>
                            </div>
                          )}
                        </Form.Item>
                      </div>
                    )}
                  </Card>

                  {/* Option 2: List Files */}
                  <Card
                    size="small"
                    className={`action-card ${
                      action === 'list-files' ? 'selected' : ''
                    }`}
                    style={{
                      borderColor:
                        action === 'list-files' ? '#FCB034' : '#d9d9d9',
                      backgroundColor:
                        action === 'list-files' ? '#FEF4E9' : '#fff',
                    }}
                  >
                    <Radio value="list-files">
                      <Space>
                        <UnorderedListOutlined
                          style={{ fontSize: 18, color: '#FCB034' }}
                        />
                        <div>
                          <Text strong>List available file parts (JSON)</Text>
                          <br />
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            GET /external/get/file/list
                          </Text>
                        </div>
                      </Space>
                    </Radio>

                    {action === 'list-files' && (
                      <div style={{ marginTop: 12, marginLeft: 24 }}>
                        <Form.Item
                          label="Date Range"
                          required
                          style={{ marginBottom: 8 }}
                        >
                          <DatePicker.RangePicker
                            key={`list-${datePickerKey}`}
                            format="DD/MM/YYYY"
                            onChange={handleDateRangeChange}
                            onCalendarChange={handleCalendarChange}
                            style={{ width: '100%' }}
                            dateRender={dateRender}
                          />
                          {availableDates.size > 0 && (
                            <div style={{ marginTop: 4 }}>
                              <Text type="secondary" style={{ fontSize: 11 }}>
                                Dates with green background have available files
                              </Text>
                            </div>
                          )}
                        </Form.Item>
                        {filteredFiles.length > 0 && (
                          <div style={{ marginTop: 4 }}>
                            <Text
                              type="secondary"
                              style={{
                                fontSize: 12,
                                display: 'block',
                                marginBottom: 4,
                              }}
                            >
                              Response preview ({filteredFiles.length} files)
                            </Text>
                            <pre
                              style={{
                                background: '#fafafa',
                                padding: 12,
                                borderRadius: 4,
                                border: '1px solid #f0f0f0',
                                fontSize: 11,
                                maxHeight: 180,
                                overflow: 'auto',
                                margin: 0,
                              }}
                            >
                              {JSON.stringify(
                                {
                                  group: selectedGroup,
                                  fileList: selectedFileList,
                                  totalFiles: filteredFiles.length,
                                  files: filteredFiles.map((f) => ({
                                    name: f.name,
                                    date: f.date,
                                  })),
                                },
                                null,
                                2
                              )}
                            </pre>
                          </div>
                        )}
                      </div>
                    )}
                  </Card>

                  {/* Option 3: Download Specific */}
                  <Card
                    size="small"
                    className={`action-card ${
                      action === 'download-specific' ? 'selected' : ''
                    }`}
                    style={{
                      borderColor:
                        action === 'download-specific' ? '#FCB034' : '#d9d9d9',
                      backgroundColor:
                        action === 'download-specific' ? '#FEF4E9' : '#fff',
                    }}
                  >
                    <Radio value="download-specific">
                      <Space>
                        <FileOutlined
                          style={{ fontSize: 18, color: '#FCB034' }}
                        />
                        <div>
                          <Text strong>Download specific file part</Text>
                          <br />
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            GET /external/get/file/part
                          </Text>
                        </div>
                      </Space>
                    </Radio>

                    {action === 'download-specific' && (
                      <div style={{ marginTop: 12, marginLeft: 24 }}>
                        <Form.Item
                          label="Date"
                          required
                          style={{ marginBottom: 8 }}
                        >
                          <Select
                            value={specificDate || undefined}
                            onChange={(val: string) => {
                              setSpecificDate(val);
                              setFileName('');
                            }}
                            placeholder={
                              categoryDates.length > 0
                                ? 'Select a date'
                                : 'No dates available'
                            }
                            disabled={categoryDates.length === 0}
                            showSearch
                            optionFilterProp="children"
                          >
                            {categoryDates.map((d) => (
                              <Select.Option key={d} value={d}>
                                {d}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                        <Form.Item
                          label="File Name"
                          required
                          style={{ marginBottom: 0 }}
                        >
                          <Select
                            value={fileName || undefined}
                            onChange={(val: string) => setFileName(val)}
                            placeholder={
                              specificDate
                                ? 'Select a file'
                                : 'Select a date first'
                            }
                            disabled={!specificDate}
                            showSearch
                            optionFilterProp="children"
                          >
                            {filesForDate.map((f) => (
                              <Select.Option key={f} value={f}>
                                {f}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </div>
                    )}
                  </Card>
                </Space>
              </Radio.Group>
            </Form.Item>
          )}

          {/* Config Mode - Date Range */}
          {mode === 'config' && (
            <Form.Item label="Date Range" required>
              <DatePicker.RangePicker
                key={`config-${datePickerKey}`}
                format="DD/MM/YYYY"
                onChange={handleDateRangeChange}
                onCalendarChange={handleCalendarChange}
                style={{ width: '100%' }}
                dateRender={dateRender}
              />
              {availableDates.size > 0 && (
                <div style={{ marginTop: 4 }}>
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    Dates with green background have available files
                  </Text>
                </div>
              )}
            </Form.Item>
          )}

          {/* Options - shown for download-all and config modes */}
          {(mode === 'config' ||
            (mode === 'direct' && action === 'download-all')) && (
            <Form.Item label="Options">
              <Checkbox
                checked={noDirectory}
                onChange={(e) => setNoDirectory(e.target.checked)}
              >
                No Directory (flatten folder structure in ZIP)
              </Checkbox>
              {mode === 'config' && (
                <div style={{ marginTop: 4 }}>
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    Note: If the saved config has a directory setting, it will
                    take priority over this option.
                  </Text>
                </div>
              )}
            </Form.Item>
          )}
        </Form>
      </Spin>

      <Divider />

      <CodeSnippetDisplay
        apiType={getApiType()}
        params={getParams()}
        disabled={!isFormComplete()}
      />
    </div>
  );
};
